import {
  has, last, noop, wrap, merge, unary,
  isArray, includes, endsWith, camelCase,
  startsWith, capitalize, isPlainObject
} from 'lodash-es'

import { safeGet, defaultTo } from './bits'

import RE from './re'
import envRaw from './env-raw'

const getExt = (v) => last(/\.([a-z0-9]{3,4})\s*$/i.exec(v))
const falsyRE = { test: () => false }

const warn = envRaw.showsPostInfoDebug
  ? console.warn.bind(console)
  : noop

// types

const TYPES = [
  { type: 'text', exts: [] },
  { type: 'video', exts: ['webm', 'mp4', 'mpeg', 'ogv'] },
  { type: 'audio', exts: ['weba', 'mp3', 'ogg', 'aac', 'wav'] },
  { type: 'image', exts: ['webp', 'jpg', 'jpeg', 'png', 'svg'] }
]

const TYPE_SIGNS = TYPES.map(({ type, exts }) => ({
  flag: camelCase(`is some ${type}`),
  type,
  exts,
  Type: capitalize(type)
}))

const AUDIO_TYPE_SIGN = {
  flag: 'isSomeAudio',
  type: 'audio',
  Type: 'Audio'
}

const VIDEO_TYPE_SIGN = {
  flag: 'isSomeVideo',
  type: 'video',
  Type: 'Video'
}

const TEXT_TYPE_SIGN = {
  flag: 'isSomeText',
  type: 'text',
  Type: 'Text'
}

// hosts

const HOSTS = [{
  host: 'youtube',
  urlRE: RE.IS_YOUTUBE,
  constTypeSign: VIDEO_TYPE_SIGN
}, {
  host: 'instagram',
  urlRE: RE.IS_INSTAGRAM
}, {
  host: 'facebook',
  urlRE: RE.IS_FACEBOOK
}, {
  host: 'vimeo',
  urlRE: RE.IS_VIMEO,
  constTypeSign: VIDEO_TYPE_SIGN
}, {
  host: 'vine',
  urlRE: RE.IS_VINE,
  constTypeSign: VIDEO_TYPE_SIGN
}, {
  host: 'soundcloud',
  urlRE: RE.IS_SOUNDCLOUD,
  constTypeSign: AUDIO_TYPE_SIGN
}, {
  host: 'twitter',
  urlRE: RE.IS_TWITTER
}, {
  host: 'pinterest',
  urlRE: RE.IS_PINTEREST
}, {
  host: 'tumblr',
  urlRE: RE.IS_TUMBLR
}, {
  host: 'upload',
  entryTypes: ['upload_action', 'image', 'video', 'audio', 'text']
}]

const HOST_SIGNS = HOSTS.map((wrap) => {
  const flag = camelCase(`is ${wrap.host} host`)
  const host = wrap.host
  const Host = capitalize(wrap.host)
  const urlRE = defaultTo(wrap.urlRE, falsyRE)
  const entryTypes = defaultTo(wrap.entryTypes, [])
  const constTypeSign = defaultTo(wrap.constTypeSign, null)
  return { flag, host, Host, urlRE, entryTypes, constTypeSign }
})

// base post info

const BASE_POST_INFO = {
  isUnknown: false,
  host: null,
  shortInfo() {
    if (this.hasOwnProperty('__short__')) {
      return this.__short__
    }

    this.__short__ = { host: this.host }
    if (this.isUnknown) this.__short__.isUnknown = this.isUnknown
    if (this.hasOwnProperty('reason')) this.__short__.reason = this.reason
    return this.__short__
  }
}

HOST_SIGNS.forEach((hostSign) => {
  BASE_POST_INFO[hostSign.flag] = false
})

function PostInfo(override = {}) {
  return merge({}, BASE_POST_INFO, override)
}

function UnknownPost(reason) {
  return PostInfo({ isUnknown: true, reason })
}

// base media info

const BASE_CONTENT_INFO = {
  isUnknown: false,
  host: null,
  type: null,
  shortInfo() {
    if (this.hasOwnProperty('__short__')) {
      return this.__short__
    }

    this.__short__ = { host: this.host, type: this.type }
    if (this.isUnknown) this.__short__.isUnknown = this.isUnknown
    if (this.hasOwnProperty('reason')) this.__short__.reason = this.reason
    return this.__short__
  }
}

HOST_SIGNS.forEach((hostSign) => TYPE_SIGNS.forEach((typeSign) => {
  BASE_CONTENT_INFO[hostSign.flag] = false
  BASE_CONTENT_INFO[typeSign.flag] = false
  BASE_CONTENT_INFO['is' + hostSign.Host + typeSign.Type] = false
}))

function MediaInfo(override = {}) {
  return merge({}, BASE_CONTENT_INFO, override)
}

function UnknownMedia(reason) {
  return MediaInfo({ isUnknown: true, reason })
}

// reject reasons

const UNKNOWN_BY_ENTRY_TYPE = '`post.entry_type` should be defined!'
const UNKNOWN_BY_DEFINED_EXTERNAL_ID = '`post.external_id` should be undefined for posts with `post.entry_type` == "upload_action"!'
const UNKNOWN_BY_UNDEFINED_EXTERNAL_ID = '`post.external_id` should be defined for posts with `post.entry_type` != "upload_action"!'
const UNKNOWN_BY_REF_HOST = 'post has unknown host!'
const UNKNOWN_BY_CNT_HOST = 'media has unknown host!'
const UNKNOWN_BY_CNT_TYPE = 'media has unknown type!'

// main

export const blankPostInfo = PostInfo({ blank: true })
export const blankMediaInfo = MediaInfo({ blank: true })

// post info

export function attachPostInfo(post) {
  if (isArray(post)) {
    post.forEach(attachPostInfo)
  } else {
    post.info = getPostInfo(post)
  }
  return post
}

export function detachPostInfo(post) {
  if (isArray(post)) {
    post.forEach(detachPostInfo)
  } else {
    delete post.info
  }
  return post
}

export function getShortPostInfo(post) {
  return getPostInfo(post).shortInfo()
}

export function getPostInfo(post) {
  return getPostInfoInternal(false, post)
}

// media info

export function attachMediaInfo(post, media) {
  if (isPlainObject(post) && isPlainObject(media)) {
    media.info = getMediaInfo(post, media)
  } else if (isArray(post)) {
    post.forEach(unary(attachMediaInfo))
  } else {
    post.media_list.forEach(unary(wrap(post, attachMediaInfo)))
  }
  return post
}

export function detachMediaInfo(entry) {
  if (isArray(entry)) {
    entry.forEach(detachMediaInfo)
  } else if (has(entry, 'media_list')) {
    entry.media_list.forEach(detachMediaInfo)
  } else {
    delete entry.info
  }
  return entry
}

export function getShortMediaInfo(post, media) {
  return getMediaInfo(post, media).shortInfo()
}

export function getMediaListInfo(post, media_list) {
  return media_list.map(unary(wrap(post, getMediaInfo)))
}

export function getMediaInfo(post, media) {
  return getPostInfoInternal(true, post, media)
}

// internal info

export function getPostInfoInternal(isMediaInfoRequested, post, media) {

  // post info
  const id = safeGet(post, 'id')
  const post_url = safeGet(post, 'post_url')
  const entry_type = has(post, 'entry_type') ? safeGet(post, 'entry_type') : safeGet(post, 'type')

  if (!entry_type) {
    warn(`getPostInfoInternal: ${UNKNOWN_BY_ENTRY_TYPE}`, id)
    return isMediaInfoRequested
      ? UnknownMedia(UNKNOWN_BY_ENTRY_TYPE)
      : UnknownPost(UNKNOWN_BY_ENTRY_TYPE)
  }

  // reference host
  let refHostSign = null

  refHostSign = HOST_SIGNS.find((hostSign) => hostSign.urlRE.test(post_url))
  if (!refHostSign) refHostSign = HOST_SIGNS.find((hostSign) => includes(hostSign.entryTypes, entry_type))
  if (!refHostSign) refHostSign = HOST_SIGNS.find((hostSign) => startsWith(entry_type, hostSign.host))

  if (!refHostSign) {
    warn(`getPostInfoInternal: ${UNKNOWN_BY_REF_HOST}`, post_url, entry_type, id)
    return isMediaInfoRequested
      ? UnknownMedia(UNKNOWN_BY_REF_HOST)
      : UnknownPost(UNKNOWN_BY_REF_HOST)
  }

  if (!isMediaInfoRequested) {
    return PostInfo({
      host: refHostSign.host,
      [refHostSign.flag]: true
    })
  }

  // media info
  const file = safeGet(media, 'file')
  const file_type = has(media, 'file_type') ? safeGet(media, 'file_type') : safeGet(media, 'content_type')
  const external_id = safeGet(media, 'external_id')
  const is_text_only = safeGet(media, 'is_text_only')
  const external_type = safeGet(media, 'external_type')

  if (!!external_id && refHostSign.host === 'upload') {
    warn(`getPostInfoInternal: ${UNKNOWN_BY_DEFINED_EXTERNAL_ID}`, external_id, entry_type, id)
    return UnknownMedia(UNKNOWN_BY_DEFINED_EXTERNAL_ID)
  }

  if (!external_id && refHostSign.host !== 'upload') {
    warn(`getPostInfoInternal: ${UNKNOWN_BY_UNDEFINED_EXTERNAL_ID}`, external_id, entry_type, id)
    return UnknownMedia(UNKNOWN_BY_UNDEFINED_EXTERNAL_ID)
  }

  // media host
  let cntHostSign = null

  if (!external_type) {
    cntHostSign = refHostSign
  } else {
    cntHostSign = HOST_SIGNS.find((hostSign) => hostSign.urlRE.test(file))
    if (!cntHostSign) cntHostSign = HOST_SIGNS.find((hostSign) => startsWith(external_type, hostSign.host))
  }

  if (!cntHostSign) {
    warn(`getPostInfoInternal: ${UNKNOWN_BY_CNT_HOST}`, file, external_type, id)
    return UnknownMedia(UNKNOWN_BY_CNT_HOST)
  }

  // media type
  let cntTypeSign = null

  if (is_text_only) {
    cntTypeSign = TEXT_TYPE_SIGN
  } else if (cntHostSign.constTypeSign) {
    cntTypeSign = cntHostSign.constTypeSign
  } else {
    if (!cntTypeSign) cntTypeSign = TYPE_SIGNS.find((typeSign) => includes(typeSign.exts, getExt(file)))
    if (!cntTypeSign) cntTypeSign = TYPE_SIGNS.find((typeSign) => startsWith(file_type, typeSign.type))
    if (!cntTypeSign) cntTypeSign = TYPE_SIGNS.find((typeSign) => endsWith(external_type || entry_type, typeSign.type))
  }

  if (!cntTypeSign) {
    warn(`getPostInfoInternal: ${UNKNOWN_BY_CNT_TYPE}`, file, file_type, id)
    return UnknownMedia(UNKNOWN_BY_CNT_TYPE)
  }

  return MediaInfo({
    host: cntHostSign.host,
    type: cntTypeSign.type,
    [cntHostSign.flag]: true,
    [cntTypeSign.flag]: true,
    ['is' + cntHostSign.Host + cntTypeSign.Type]: true
  })
}
